import { SignupScenario } from '@src/const/signupScenarios';
import { logger } from '@src/lib/logger';
import { useSignupStore } from '@stores/signupStore';
import { appendVueRoute, Role } from '@therame/shared';
import { type RouteRecordRaw } from 'vue-router';
import { type RestrictedRecordRaw } from '..';

const routes: Array<RouteRecordRaw> = [
  {
    ...appendVueRoute<'signup'>('signup', '/signup'),
    component: () => import('@src/views/Signup.vue'),
    meta: {
      layout: 'none',
      title: 'Registrierung',
      requiresAuth: true,
      requiresOnboarding: false,
    },
    beforeEnter: () => {
      const signupStore = useSignupStore();
      signupStore.setScenario(SignupScenario.ADMIN_NEW);
    },
  },
  {
    ...appendVueRoute<'tanLogin'>('tanLogin', '/tan-login'),
    component: () => import('@src/views/TanLogin.vue'),
    meta: {
      layout: 'none',
      title: 'TAN Login',
      requiresAuth: false,
      requiresOnboarding: false,
    },
  },
  {
    ...appendVueRoute<'signupPraxis'>('signupPraxis', '/signup/praxis'),
    component: () => import('@src/views/SignupPraxis.vue'),
    meta: {
      layout: 'none',
      title: 'Registrierung',
      requiresAuth: false,
      requiresOnboarding: false,
    },
    beforeEnter: () => {
      const signupStore = useSignupStore();
      signupStore.setScenario(SignupScenario.ADMIN_NEW);
    },
  },
  {
    ...appendVueRoute<'signupPrevention'>('signupPrevention', '/signup/prevention'),
    component: () => import('@src/views/SignupPrevention.vue'),
    meta: {
      layout: 'none',
      title: 'Registrierung',
      requiresAuth: false,
      requiresOnboarding: false,
    },
    beforeEnter: () => {
      const signupStore = useSignupStore();
      signupStore.setScenario(SignupScenario.PREVENTION);
    },
  },
  {
    ...appendVueRoute<'signupSentEmail'>('signupSentEmail', '/signup-sent-email'),
    props: true,
    component: () => import('@src/views/Signup.vue'),
    meta: {
      layout: 'none',
      title: 'Registrierung',
      requiresAuth: false,
      requiresOnboarding: false,
    },
    beforeEnter: () => {
      const signupStore = useSignupStore();
      const scenario = signupStore.getScenario();
      logger.info({
        msg: 'Scenario',
        scenario,
        isIncluded: [
          SignupScenario.ADMIN_NEW,
          SignupScenario.PATIENT_NEW,
          SignupScenario.PREVENTION,
        ].includes(scenario),
      });
      if (
        [SignupScenario.ADMIN_NEW, SignupScenario.PATIENT_NEW, SignupScenario.PREVENTION].includes(
          scenario,
        )
      ) {
        signupStore.step = 1;
      }
    },
  },
  {
    ...appendVueRoute<'callAccept'>('callAccept', '/call-accept/:patientId/:inviteToken'),
    component: () => import('@src/views/CallAttendance.vue'),
    props: true,
    meta: {
      layout: 'none',
      title: 'Einladung annehmen',
      requiresAuth: false,
      requiresOnboarding: false,
    },
  },
  {
    ...appendVueRoute<'callDecline'>('callDecline', '/call-decline/:patientId/:inviteToken'),
    component: () => import('@src/views/CallAttendance.vue'),
    props: true,
    meta: {
      layout: 'none',
      title: 'Einladung ablehnen',
      requiresAuth: false,
      requiresOnboarding: false,
    },
  },
  {
    ...appendVueRoute<'verifyEmailUpdate'>('verifyEmailUpdate', '/verify-email-update/:token'),
    component: () => import('@src/views/VerifyEmailUpdate.vue'),
    props: true,
    meta: {
      layout: 'none',
      title: 'Neue E-Mail Adresse verifizieren',
      requiresAuth: false,
      requiresOnboarding: false,
    },
  },
  {
    ...appendVueRoute<'newsletterAgreement'>('newsletterAgreement', '/newsletter-agreement'),
    component: () => import('@src/views/NewsletterAgreement.vue'),
    props: true,
    meta: {
      layout: 'none',
      title: 'Zustimmung Newsletter',
      requiresAuth: true,
      requiresOnboarding: true,
      accessPossibleWithRoles: [Role.ADMIN, Role.THERAPIST],
    },
  },
  {
    // „Praxis wählen“ (#1822): only while the Account has several offices and no
    // valid stored choice; the router guard sends every other route here then.
    ...appendVueRoute<'officePicker'>('officePicker', '/praxis-waehlen'),
    component: () => import('@src/views/OfficePicker.vue'),
    meta: {
      layout: 'none',
      title: 'Praxis wählen',
      requiresAuth: true,
      requiresOnboarding: false,
    },
  },
  {
    // The acceptance step (#1824): the Person of the Active office accepts what
    // it hasn't yet. The router guard sends every other route here meanwhile.
    ...appendVueRoute<'legalAcceptance'>('legalAcceptance', '/zustimmung'),
    component: () => import('@src/views/LegalAcceptance.vue'),
    meta: {
      layout: 'none',
      title: 'Zustimmung',
      requiresAuth: true,
      requiresOnboarding: false,
    },
  },
  {
    // The join mail's link (#1825): no id, token or address. The router guard
    // turns it into the acceptance modal over the app; this view never renders.
    ...appendVueRoute<'joinInvite'>('joinInvite', '/einladung'),
    component: { render: () => null },
    meta: {
      layout: 'none',
      title: 'Einladung',
      requiresAuth: true,
      requiresOnboarding: false,
    },
  },
  {
    // „Praxis eröffnen“ (#1832): any Account opens an office of its own. Reached
    // from the profile menu, „Praxis wählen“ and the page for an Account
    // without any office; it needs no Person, so no office either.
    ...appendVueRoute<'openOffice'>('openOffice', '/praxis-eroeffnen'),
    component: () => import('@src/views/OpenOffice.vue'),
    meta: {
      layout: 'none',
      title: 'Praxis eröffnen',
      requiresAuth: true,
      requiresOnboarding: false,
      hideActingForBand: true,
    },
  },
  {
    // The Account has no Person a session could act as (#1832): „Praxis
    // eröffnen“ or „Abmelden“. The router guard sends every other route here.
    ...appendVueRoute<'accountWithoutOffice'>('accountWithoutOffice', '/ohne-praxis'),
    component: () => import('@src/views/AccountWithoutOffice.vue'),
    meta: {
      layout: 'none',
      title: 'Keine Praxis',
      requiresAuth: true,
      requiresOnboarding: false,
    },
  },
  {
    // The join page of an office location's registration link (#1834), where
    // its QR code leads. It needs an Account, not a Person: the router guard
    // sends a logged-out visitor to the registration form and back here.
    ...appendVueRoute<'joinOffice'>('joinOffice', '/join/:token'),
    component: () => import('@src/views/JoinOffice.vue'),
    meta: {
      layout: 'none',
      title: 'Praxis beitreten',
      requiresAuth: true,
      requiresOnboarding: false,
    },
  },
] satisfies RestrictedRecordRaw[];

export default routes;
